import React, { useRef, useState } from "react";
import {
  Box, Paper, Typography, Tabs, Tab, TextField, Button, Stack, Chip, MenuItem,
  Checkbox, FormControlLabel, Divider, Grid, Autocomplete, Avatar,
} from "@mui/material";
import { ArrowRight, Paperclip, Upload } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { ticketService } from "../../api/services/ticketService";
import { slaService } from "../../api/services/slaService";
import { planService } from "../../api/services/planService";
import { identityService } from "../../api/services/identityService";
import { PageHeader, Loader, StatusChip, EmptyState } from "../../components/Common";
import { formatFileSize } from "../../utils/downloadBlob";
import { avatarColor, initials } from "../../utils/avatarColor";
import AttachmentPreviewDialog from "../../components/AttachmentPreviewDialog";

function TabPanel({ value, index, children }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

export default function TicketDetail() {
  const { ticketId } = useParams();
  const id = Number(ticketId);
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [tab, setTab] = useState(0);

  const { data: ticket, isLoading } = useQuery({ queryKey: ["ticket", id], queryFn: () => ticketService.get(id) });
  const { data: statuses } = useQuery({ queryKey: ["statuses"], queryFn: slaService.listStatuses, staleTime: Infinity });
  const { data: priorities } = useQuery({ queryKey: ["priorities"], queryFn: slaService.listPriorities, staleTime: Infinity });
  const { data: transitions } = useQuery({ queryKey: ["workflow-transitions"], queryFn: slaService.listTransitions, staleTime: Infinity });
  const { data: plans } = useQuery({
    queryKey: ["plans", { project_id: ticket?.project_id }],
    queryFn: () => planService.list({ project_id: ticket.project_id }),
    enabled: Boolean(ticket?.project_id),
  });
  const { data: employees } = useQuery({ queryKey: ["employees"], queryFn: identityService.listEmployees });
  const { data: teams } = useQuery({ queryKey: ["teams"], queryFn: identityService.listTeams });

  const invalidateTicket = () => queryClient.invalidateQueries({ queryKey: ["ticket", id] });

  const updateMutation = useMutation({
    mutationFn: (payload) => ticketService.update(id, payload),
    onSuccess: () => {
      enqueueSnackbar("Ticket updated", { variant: "success" });
      invalidateTicket();
    },
    onError: (err) => enqueueSnackbar(err.response?.data?.detail || "Update failed", { variant: "error" }),
  });

  if (isLoading) return <Loader label="Loading ticket..." />;
  if (!ticket) return <EmptyState title="Ticket not found" />;

  const currentStatus = statuses?.find((s) => s.id === ticket.status_id);

  return (
    <Box>
      <PageHeader
        title={`${ticket.ticket_number} — ${ticket.subject}`}
        subtitle={`Created ${new Date(ticket.created_at).toLocaleString()}`}
        action={<StatusChip value={currentStatus?.state_group} label={currentStatus?.name} />}
      />

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper variant="outlined">
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2, borderBottom: "1px solid #eee" }}>
              <Tab label="Details" />
              <Tab label="Comments" />
              <Tab label="Attachments" />
              <Tab label="History" />
              <Tab label="Watchers" />
            </Tabs>
            <Box sx={{ p: 2.5 }}>
              <TabPanel value={tab} index={0}>
                <DetailsTab ticket={ticket} onSave={(payload) => updateMutation.mutate(payload)} />
              </TabPanel>
              <TabPanel value={tab} index={1}>
                <CommentsTab ticketId={id} />
              </TabPanel>
              <TabPanel value={tab} index={2}>
                <AttachmentsTab ticketId={id} />
              </TabPanel>
              <TabPanel value={tab} index={3}>
                <HistoryTab
                  ticketId={id} employees={employees} statuses={statuses}
                  priorities={priorities} plans={plans} teams={teams}
                />
              </TabPanel>
              <TabPanel value={tab} index={4}>
                <WatchersTab ticketId={id} employees={employees} />
              </TabPanel>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Stack spacing={2}>
            <StatusPriorityCard
              ticket={ticket} statuses={statuses} priorities={priorities}
              transitions={transitions} plans={plans} employees={employees}
              onChange={(payload) => updateMutation.mutate(payload)}
            />
            <SlaCard ticketId={id} />
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}

function DetailsTab({ ticket, onSave }) {
  const [subject, setSubject] = useState(ticket.subject || "");
  const [description, setDescription] = useState(ticket.description || "");
  const [resolutionNotes, setResolutionNotes] = useState(ticket.resolution_notes || "");
  const [rootCause, setRootCause] = useState(ticket.root_cause || "");

  return (
    <Stack spacing={2}>
      <TextField label="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} fullWidth required />
      <TextField label="Description" value={description} onChange={(e) => setDescription(e.target.value)} multiline minRows={3} fullWidth />
      <TextField label="Resolution Notes" value={resolutionNotes} onChange={(e) => setResolutionNotes(e.target.value)} multiline minRows={2} fullWidth />
      <TextField label="Root Cause" value={rootCause} onChange={(e) => setRootCause(e.target.value)} multiline minRows={2} fullWidth />
      <Box>
        <Button
          variant="contained"
          disabled={!subject.trim()}
          onClick={() => onSave({ subject, description, resolution_notes: resolutionNotes, root_cause: rootCause })}
        >
          Save Changes
        </Button>
      </Box>
    </Stack>
  );
}

function StatusPriorityCard({ ticket, statuses, priorities, transitions, plans, employees, onChange }) {
  // Client-side UX only -- narrows the dropdown to legal next statuses per
  // the configured workflow. The server's own check is the real gate (an
  // empty transitions list means the company hasn't configured one yet, so
  // every status stays selectable, matching the backend's allow-all fallback).
  const statusOptions = (statuses || []).filter((s) => {
    if (s.id === ticket.status_id) return true;
    if (!transitions || transitions.length === 0) return true;
    return transitions.some((t) => t.from_status_id === ticket.status_id && t.to_status_id === s.id);
  });

  // Only not_started plans are legal move targets (server enforces this too) --
  // but keep the ticket's current plan in the list even if it has since started,
  // so the select still shows the right value instead of falling back to blank.
  const currentPlan = (plans || []).find((p) => p.id === ticket.plan_id);
  const planOptions = (plans || []).filter((p) => p.status === "not_started" || p.id === ticket.plan_id);

  return (
    <Paper variant="outlined" sx={{ p: 2.5 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>Status & Priority</Typography>
      <Stack spacing={2}>
        <TextField
          select label="Status" size="small" value={ticket.status_id}
          onChange={(e) => onChange({ status_id: Number(e.target.value) })}
        >
          {statusOptions.map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
        </TextField>
        <TextField
          select label="Priority" size="small" value={ticket.priority_id || ""}
          onChange={(e) => onChange({ priority_id: Number(e.target.value) })}
        >
          {(priorities || []).map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
        </TextField>
        <TextField
          select label="Phase" size="small" value={ticket.plan_id || ""}
          disabled={currentPlan && currentPlan.status !== "not_started"}
          helperText={currentPlan && currentPlan.status !== "not_started" ? "Locked -- phase has already started" : undefined}
          onChange={(e) => onChange({ plan_id: e.target.value ? Number(e.target.value) : null })}
        >
          <MenuItem value="">Backlog</MenuItem>
          {planOptions.map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
        </TextField>
        <Autocomplete
          size="small"
          options={employees || []}
          getOptionLabel={(e) => e.name || e.email}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          value={(employees || []).find((e) => e.id === ticket.assignee_employee_id) || null}
          onChange={(_, value) => onChange({ assignee_employee_id: value ? value.id : null })}
          renderInput={(params) => <TextField {...params} label="Assignee" placeholder="Unassigned" />}
        />
      </Stack>
    </Paper>
  );
}

function SlaCard({ ticketId }) {
  const { data: sla, isLoading, isError } = useQuery({
    queryKey: ["ticket-sla", ticketId],
    queryFn: () => ticketService.sla(ticketId),
    retry: false,
  });

  return (
    <Paper variant="outlined" sx={{ p: 2.5 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>SLA</Typography>
      {isLoading && <Typography variant="body2" color="text.secondary">Loading...</Typography>}
      {isError && <Typography variant="body2" color="text.secondary">No SLA timer (priority not set, or no matching policy).</Typography>}
      {sla && (
        <Stack spacing={1}>
          <Row label="Response due" value={sla.response_due_at ? new Date(sla.response_due_at).toLocaleString() : "-"} />
          <Row label="Resolution due" value={sla.resolution_due_at ? new Date(sla.resolution_due_at).toLocaleString() : "-"} />
          <Row label="Response met" value={sla.response_met === null ? "Pending" : sla.response_met ? "Yes" : "Breached"} />
          <Row label="Resolution met" value={sla.resolution_met === null ? "Pending" : sla.resolution_met ? "Yes" : "Breached"} />
          {sla.is_paused && <Chip size="small" label="Paused" sx={{ alignSelf: "flex-start" }} />}
          {sla.escalated && <Chip size="small" color="error" label="Escalated" sx={{ alignSelf: "flex-start" }} />}
        </Stack>
      )}
    </Paper>
  );
}

function Row({ label, value }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>{value}</Typography>
    </Box>
  );
}

function CommentsTab({ ticketId }) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [body, setBody] = useState("");
  const [isInternal, setIsInternal] = useState(false);

  const { data: comments, isLoading } = useQuery({
    queryKey: ["ticket-comments", ticketId],
    queryFn: () => ticketService.listComments(ticketId),
  });

  const addMutation = useMutation({
    mutationFn: () => ticketService.addComment(ticketId, { body, is_internal: isInternal }),
    onSuccess: () => {
      setBody("");
      enqueueSnackbar("Comment added", { variant: "success" });
      queryClient.invalidateQueries({ queryKey: ["ticket-comments", ticketId] });
    },
  });

  return (
    <Stack spacing={2}>
      <TextField
        label="Add a comment" value={body} onChange={(e) => setBody(e.target.value)}
        multiline minRows={2} fullWidth
      />
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <FormControlLabel
          control={<Checkbox checked={isInternal} onChange={(e) => setIsInternal(e.target.checked)} />}
          label="Internal note"
        />
        <Button variant="contained" disabled={!body.trim() || addMutation.isPending} onClick={() => addMutation.mutate()}>
          Post
        </Button>
      </Stack>

      <Divider />

      {isLoading && <Loader label="Loading comments..." />}
      {!isLoading && (!comments || comments.length === 0) && <EmptyState title="No comments yet" />}
      <Stack spacing={1.5}>
        {(comments || []).map((c) => (
          <Paper key={c.id} variant="outlined" sx={{ p: 1.5, bgcolor: c.is_internal ? "#FFF9E6" : "transparent" }}>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                {c.author_employee_id ? `Employee #${c.author_employee_id}` : `Client #${c.author_client_contact_id}`}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                {c.is_internal && <Chip size="small" label="Internal" />}
                <Typography variant="caption" color="text.secondary">{new Date(c.created_at).toLocaleString()}</Typography>
              </Stack>
            </Stack>
            <Typography variant="body2">{c.body}</Typography>
          </Paper>
        ))}
      </Stack>
    </Stack>
  );
}

function AttachmentsTab({ ticketId }) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const fileInputRef = useRef(null);

  const { data: attachments, isLoading } = useQuery({
    queryKey: ["ticket-attachments", ticketId],
    queryFn: () => ticketService.listAttachments(ticketId),
  });

  const uploadMutation = useMutation({
    mutationFn: () => ticketService.uploadAttachment(ticketId, selectedFile),
    onSuccess: () => {
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      enqueueSnackbar("File uploaded", { variant: "success" });
      queryClient.invalidateQueries({ queryKey: ["ticket-attachments", ticketId] });
    },
    onError: (err) => enqueueSnackbar(err.response?.data?.detail || "Upload failed", { variant: "error" }),
  });

  const handleOpen = (a) => setPreviewAttachment(a);

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Button component="label" variant="outlined" startIcon={<Paperclip size={16} />}>
          Choose File
          <input
            ref={fileInputRef} type="file" hidden
            onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
          />
        </Button>
        {selectedFile && (
          <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
            {selectedFile.name} ({formatFileSize(selectedFile.size)})
          </Typography>
        )}
        <Button
          variant="contained" startIcon={<Upload size={16} />}
          disabled={!selectedFile || uploadMutation.isPending}
          onClick={() => uploadMutation.mutate()}
        >
          {uploadMutation.isPending ? "Uploading..." : "Upload"}
        </Button>
      </Stack>
      <Typography variant="caption" color="text.secondary">
        Any document, image, or spreadsheet up to 15MB.
      </Typography>

      <Divider />

      {isLoading && <Loader label="Loading attachments..." />}
      {!isLoading && (!attachments || attachments.length === 0) && <EmptyState title="No attachments yet" />}
      <Stack spacing={1}>
        {(attachments || []).map((a) => (
          <Paper key={a.id} variant="outlined" sx={{ p: 1.5, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
              <Paperclip size={16} />
              <Button
                variant="text" onClick={() => handleOpen(a)}
                sx={{ p: 0, minWidth: 0, textTransform: "none", fontWeight: 600, color: "#000", textDecoration: "underline" }}
              >
                {a.file_name}
              </Button>
              {a.file_size != null && (
                <Typography variant="caption" color="text.secondary">{formatFileSize(a.file_size)}</Typography>
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, pl: 2 }}>
              {new Date(a.created_at).toLocaleString()}
            </Typography>
          </Paper>
        ))}
      </Stack>

      <AttachmentPreviewDialog ticketId={ticketId} attachment={previewAttachment} onClose={() => setPreviewAttachment(null)} />
    </Stack>
  );
}

const FIELD_LABELS = {
  status_id: "Status",
  status: "Status",
  priority_id: "Priority",
  assignee_employee_id: "Assignee",
  requester_employee_id: "Requester",
  plan_id: "Phase",
  team_id: "Team",
  due_date: "Due Date",
  estimated_hours: "Estimated Hours",
  actual_hours: "Actual Hours",
};

function fieldLabel(field) {
  return FIELD_LABELS[field] || field.replace(/_id$/, "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}


const STATUS_BORDER_COLORS = { open: "#757575", pending: "#8A6D00", resolved: "#1B5E20", closed: "#111111" };

function PersonMini({ employee }) {
  if (!employee) return <Typography variant="body2" color="text.secondary">Unassigned</Typography>;
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Avatar sx={{ width: 22, height: 22, fontSize: 11, bgcolor: avatarColor(employee.name) }}>{initials(employee.name)}</Avatar>
      <Typography variant="body2">{employee.name}</Typography>
    </Stack>
  );
}

function ValueTransition({ field, oldValue, newValue, lookups }) {
  const normalized = field === "status" ? "status_id" : field;
  const toNum = (v) => (v === null || v === undefined || v === "" ? null : Number(v));
  const oldNum = toNum(oldValue);
  const newNum = toNum(newValue);

  if (normalized === "status_id") {
    const oldStatus = lookups.statusById[oldNum];
    const newStatus = lookups.statusById[newNum];
    return (
      <Stack direction="row" spacing={1.5} alignItems="center">
        {oldStatus ? (
          <Chip size="small" variant="outlined" label={oldStatus.name}
            sx={{ borderColor: STATUS_BORDER_COLORS[oldStatus.state_group] || "#999", fontWeight: 700, textTransform: "uppercase", fontSize: 11 }} />
        ) : <Typography variant="body2" color="text.secondary">—</Typography>}
        <ArrowRight size={14} />
        {newStatus ? (
          <Chip size="small" variant="outlined" label={newStatus.name}
            sx={{ borderColor: STATUS_BORDER_COLORS[newStatus.state_group] || "#999", fontWeight: 700, textTransform: "uppercase", fontSize: 11 }} />
        ) : <Typography variant="body2" color="text.secondary">—</Typography>}
      </Stack>
    );
  }

  if (normalized === "priority_id") {
    const oldP = lookups.priorityById[oldNum];
    const newP = lookups.priorityById[newNum];
    const dot = (p) => p && <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: p.color }} />;
    return (
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Stack direction="row" spacing={0.75} alignItems="center">{dot(oldP)}<Typography variant="body2">{oldP?.name || "—"}</Typography></Stack>
        <ArrowRight size={14} />
        <Stack direction="row" spacing={0.75} alignItems="center">{dot(newP)}<Typography variant="body2">{newP?.name || "—"}</Typography></Stack>
      </Stack>
    );
  }

  if (normalized === "assignee_employee_id" || normalized === "requester_employee_id") {
    return (
      <Stack direction="row" spacing={1.5} alignItems="center">
        <PersonMini employee={lookups.employeeById[oldNum]} />
        <ArrowRight size={14} />
        <PersonMini employee={lookups.employeeById[newNum]} />
      </Stack>
    );
  }

  if (normalized === "plan_id") {
    const label = (n) => (n ? lookups.planById[n]?.name || `#${n}` : "Backlog");
    return (
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Chip size="small" variant="outlined" label={label(oldNum)} />
        <ArrowRight size={14} />
        <Chip size="small" variant="outlined" label={label(newNum)} />
      </Stack>
    );
  }

  if (normalized === "team_id") {
    const label = (n) => (n ? lookups.teamById[n]?.name || `#${n}` : "—");
    return (
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Chip size="small" variant="outlined" label={label(oldNum)} />
        <ArrowRight size={14} />
        <Chip size="small" variant="outlined" label={label(newNum)} />
      </Stack>
    );
  }

  return <Typography variant="body2">{oldValue ?? "—"} → {newValue ?? "—"}</Typography>;
}

function HistoryEntry({ entry, lookups }) {
  const actor = entry.changed_by_employee_id ? lookups.employeeById[entry.changed_by_employee_id] : null;
  const actorName = actor?.name || "System";

  return (
    <Stack direction="row" spacing={1.5}>
      <Avatar sx={{ width: 32, height: 32, fontSize: 13, fontWeight: 700, bgcolor: avatarColor(actorName) }}>
        {initials(actorName)}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2">
          <strong>{actorName}</strong> changed the <strong>{fieldLabel(entry.field_changed)}</strong>
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {formatDistanceToNow(new Date(entry.changed_at), { addSuffix: true })}
        </Typography>
        <Box sx={{ mt: 1 }}>
          <ValueTransition field={entry.field_changed} oldValue={entry.old_value} newValue={entry.new_value} lookups={lookups} />
        </Box>
      </Box>
    </Stack>
  );
}

function HistoryTab({ ticketId, employees, statuses, priorities, plans, teams }) {
  const { data: history, isLoading } = useQuery({
    queryKey: ["ticket-history", ticketId],
    queryFn: () => ticketService.history(ticketId),
  });

  if (isLoading) return <Loader label="Loading history..." />;
  if (!history || history.length === 0) return <EmptyState title="No changes recorded yet" />;

  const lookups = {
    employeeById: Object.fromEntries((employees || []).map((e) => [e.id, e])),
    statusById: Object.fromEntries((statuses || []).map((s) => [s.id, s])),
    priorityById: Object.fromEntries((priorities || []).map((p) => [p.id, p])),
    planById: Object.fromEntries((plans || []).map((p) => [p.id, p])),
    teamById: Object.fromEntries((teams || []).map((t) => [t.id, t])),
  };

  const sorted = [...history].sort((a, b) => new Date(b.changed_at) - new Date(a.changed_at));

  return (
    <Stack spacing={3}>
      {sorted.map((h) => <HistoryEntry key={h.id} entry={h} lookups={lookups} />)}
    </Stack>
  );
}

function WatchersTab({ ticketId, employees }) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [selected, setSelected] = useState(null);

  const { data: watchers, isLoading } = useQuery({
    queryKey: ["ticket-watchers", ticketId],
    queryFn: () => ticketService.listWatchers(ticketId),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["ticket-watchers", ticketId] });

  const addMutation = useMutation({
    mutationFn: () => ticketService.addWatcher(ticketId, selected.id),
    onSuccess: () => {
      setSelected(null);
      enqueueSnackbar("Watcher added", { variant: "success" });
      invalidate();
    },
    onError: (err) => enqueueSnackbar(err.response?.data?.detail || "Couldn't add watcher", { variant: "error" }),
  });

  const removeMutation = useMutation({
    mutationFn: (employeeId) => ticketService.removeWatcher(ticketId, employeeId),
    onSuccess: () => { enqueueSnackbar("Watcher removed", { variant: "success" }); invalidate(); },
    onError: (err) => enqueueSnackbar(err.response?.data?.detail || "Couldn't remove watcher", { variant: "error" }),
  });

  const watchingIds = new Set((watchers || []).map((w) => w.employee_id));
  const employeeById = Object.fromEntries((employees || []).map((e) => [e.id, e]));
  const availableEmployees = (employees || []).filter((e) => !watchingIds.has(e.id));

  return (
    <Stack spacing={2}>
      <Typography variant="caption" color="text.secondary">
        Watchers get visibility into this ticket without being the assignee.
      </Typography>
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <Autocomplete
          size="small" fullWidth
          options={availableEmployees}
          getOptionLabel={(e) => e.name || e.email}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          value={selected}
          onChange={(_, value) => setSelected(value)}
          renderInput={(params) => <TextField {...params} label="Add a watcher" placeholder="Search by name" />}
        />
        <Button variant="contained" disabled={!selected || addMutation.isPending} onClick={() => addMutation.mutate()}>
          Add
        </Button>
      </Stack>

      <Divider />

      {isLoading && <Loader label="Loading watchers..." />}
      {!isLoading && (!watchers || watchers.length === 0) && <EmptyState title="No watchers yet" />}
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {(watchers || []).map((w) => (
          <Chip
            key={w.id}
            label={employeeById[w.employee_id]?.name || `Employee #${w.employee_id}`}
            onDelete={() => removeMutation.mutate(w.employee_id)}
          />
        ))}
      </Stack>
    </Stack>
  );
}
